import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LucideIcon } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useRef } from "react";

export type MultiToggleOption<T> = {
  label: string;
  icon?: LucideIcon;
  value: T;
  disabled?: boolean;
  disabledReason?: string;
};

export type MultiToggleProps<T> = {
  "aria-label"?: string;
  "aria-labelledby"?: string;
  className?: string;
  value: T;
  onChange: (value: T) => void;
  options: MultiToggleOption<T>[];
};

export function MultiToggle<T>({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  className,
  options,
  onChange,
  value,
}: MultiToggleProps<T>) {
  const optionButtonsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0;

  function selectOption(index: number) {
    const option = options[index];

    if (!option || option.disabled) {
      return;
    }

    onChange(option.value);
    optionButtonsRef.current[index]?.focus();
  }

  function findNextEnabledIndex(startIndex: number, step: number): number {
    for (let offset = 1; offset <= options.length; offset++) {
      const index = (startIndex + step * offset + options.length * offset) % options.length;
      if (!options[index]?.disabled) {
        return index;
      }
    }

    return startIndex;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (options.length === 0) {
      return;
    }

    const focusedIndex = optionButtonsRef.current.findIndex(
      (optionRef) => optionRef === event.currentTarget.ownerDocument.activeElement,
    );
    const currentIndex = focusedIndex >= 0 ? focusedIndex : activeIndex;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        selectOption(findNextEnabledIndex(currentIndex, 1));
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        selectOption(findNextEnabledIndex(currentIndex, -1));
        break;
      case "End":
        event.preventDefault();
        selectOption(findNextEnabledIndex(options.length, -1));
        break;
      case "Home":
        event.preventDefault();
        selectOption(findNextEnabledIndex(-1, 1));
        break;
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onKeyDown={handleKeyDown}
      className={cn("bg-background border-border relative grid overflow-hidden rounded-lg border text-sm", className)}
      style={options.length > 0 ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined}
    >
      {options.length > 0 && (
        <div
          aria-hidden="true"
          className="bg-primary absolute top-0 left-0 z-0 h-full rounded-lg transition-all duration-200"
          style={{
            width: `${100 / options.length}%`,
            left: `${(100 / options.length) * activeIndex}%`,
          }}
        />
      )}
      {options.map((option, index) => {
        const button = (
          <button
            ref={(node) => {
              optionButtonsRef.current[index] = node;
            }}
            role="radio"
            aria-checked={option.value === value}
            aria-disabled={option.disabled}
            disabled={option.disabled}
            tabIndex={index === activeIndex ? 0 : -1}
            className={cn(
              "relative z-10 flex w-full flex-row items-center justify-center gap-2 rounded-lg px-4 py-2 whitespace-nowrap transition-colors duration-200",
              option.disabled
                ? "text-muted-foreground cursor-not-allowed opacity-50"
                : "hover:bg-primary/10 cursor-pointer",
              option.value === value && "text-primary-foreground",
            )}
            type="button"
            onClick={() => {
              if (!option.disabled) {
                onChange(option.value);
              }
            }}
          >
            {option.icon && <option.icon className="h-4 w-4" />}
            {option.label}
          </button>
        );

        return (
          <MultiToggleOptionWrapper
            key={option.label}
            disabledReason={option.disabled ? option.disabledReason : undefined}
          >
            {button}
          </MultiToggleOptionWrapper>
        );
      })}
    </div>
  );
}

function MultiToggleOptionWrapper({ disabledReason, children }: { disabledReason?: string; children: ReactNode }) {
  if (!disabledReason) {
    return <div className="flex">{children}</div>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex">{children}</div>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
